import { useEffect, useMemo, useState, type DragEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { usePerspective } from "../lib/perspective";
import {
  useKanbanBoard, useCreateCard, useMoveCard, useDeleteCard, useSaveKanbanConfig,
  useReorderCards, useWritebackApprovals, useDecideWritebackApproval,
  SOURCE_LABEL, type KanbanCard, type KanbanColumn, type BoardFilters, type Lane,
  type WritebackApproval,
} from "../lib/kanban";

const VIEW_ID = "default";
const SOURCE_CLASS: Record<string, string> = {
  wp: "kb-src-wp", run: "kb-src-run", decision: "kb-src-decision",
  naechste: "kb-src-naechste", eigen: "kb-src-eigen",
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function isOverdue(card: KanbanCard): boolean {
  return Boolean(card.due_date) && card.due_date! < todayIso() && card.lane !== "erledigt";
}

function CardLinks({ links }: { links?: KanbanCard["links"] }) {
  if (!links?.length) return null;
  return (
    <div className="kb-card-links">
      {links.map((link, index) => link.href.startsWith("http") ? (
        <a key={index} href={link.href} target="_blank" rel="noopener noreferrer" className="kb-link">
          {link.label} ↗
        </a>
      ) : (
        <Link key={index} to={link.href} className="kb-link">{link.label} →</Link>
      ))}
    </div>
  );
}

function approvalStatus(value: WritebackApproval["status"]): string {
  if (value === "freigegeben_wartet_auf_op_write") return "Freigegeben, wartet auf OP-Write";
  if (value === "abgelehnt") return "Abgelehnt";
  if (value === "storniert") return "Storniert";
  return "Offen";
}

export default function KanbanPage() {
  const { perspective } = usePerspective();
  const role = perspective === "kunde" ? "kunde" : "berater";
  const [filters, setFilters] = useState<BoardFilters>({
    ressort: "", programm: "", type: "", q: "", include_next: true, view: VIEW_ID,
  });
  const board = useKanbanBoard(role, filters);
  const create = useCreateCard();
  const move = useMoveCard();
  const del = useDeleteCard();
  const saveConfig = useSaveKanbanConfig();
  const reorder = useReorderCards();
  const approvals = useWritebackApprovals("all");
  const decide = useDecideWritebackApproval();

  const [dragged, setDragged] = useState<KanbanCard | null>(null);
  const [overLane, setOverLane] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showApprovals, setShowApprovals] = useState(() => new URLSearchParams(window.location.search).get("approvals") === "1");
  const [configColumns, setConfigColumns] = useState<Lane[]>([]);
  const [swimlanes, setSwimlanes] = useState(false);
  const [approvalMove, setApprovalMove] = useState<{ card: KanbanCard; lane: string } | null>(null);
  const [approvalReason, setApprovalReason] = useState("");
  const [newCard, setNewCard] = useState({ title: "", body: "", lane: "offen", ressort: "", due_date: "" });

  const data = board.data;
  const available = data?.available_filters;

  useEffect(() => {
    if (!data?.config) return;
    setConfigColumns(data.config.columns.map((column) => ({ ...column })));
    setSwimlanes(Boolean(data.config.swimlanes_enabled));
  }, [data?.config]);

  const legend = useMemo(() => {
    const counts = data?.source_counts ?? {};
    return (Object.keys(SOURCE_LABEL) as (keyof typeof SOURCE_LABEL)[])
      .filter((key) => (counts[key] ?? 0) > 0)
      .map((key) => ({ key, label: SOURCE_LABEL[key], count: counts[key] ?? 0 }));
  }, [data]);

  function moveAppCard(card: KanbanCard, lane: string) {
    move.mutate(
      { id: card.id, lane, view_id: VIEW_ID },
      {
        onSuccess: (result) => {
          if (!result.ok) setNotice(result.reason ?? "Verschieben nicht möglich.");
        },
        onError: () => setNotice("Verschieben fehlgeschlagen."),
      },
    );
  }

  function dropIntoLane(lane: string) {
    setOverLane(null);
    const card = dragged;
    setDragged(null);
    if (!card) return;
    setNotice("");
    if (card.lane === lane) {
      const column = data?.columns.find((item) => item.key === lane);
      if (column) reorder.mutate({ lane, ordered_ids: [...column.cards.map((item) => item.id).filter((id) => id !== card.id), card.id], view_id: VIEW_ID });
      return;
    }
    if (card.source === "wp") {
      setApprovalMove({ card, lane });
      setApprovalReason("");
      return;
    }
    if (card.source === "naechste") {
      setNotice("Abgeleitete nächste Schritte können nicht in eine andere Spalte verschoben werden.");
      return;
    }
    moveAppCard(card, lane);
  }

  function dropBefore(event: DragEvent, target: KanbanCard, column: KanbanColumn) {
    event.preventDefault();
    event.stopPropagation();
    const card = dragged;
    setDragged(null);
    setOverLane(null);
    if (!card) return;
    if (card.lane !== column.key) {
      if (card.source === "wp") {
        setApprovalMove({ card, lane: column.key });
        setApprovalReason("");
      } else if (card.source === "naechste") {
        setNotice("Abgeleitete nächste Schritte können nicht in eine andere Spalte verschoben werden.");
      } else {
        moveAppCard(card, column.key);
      }
      return;
    }
    if (card.id === target.id) return;
    const ids = column.cards.map((item) => item.id).filter((id) => id !== card.id);
    const targetIndex = Math.max(0, ids.indexOf(target.id));
    ids.splice(targetIndex, 0, card.id);
    reorder.mutate({ lane: column.key, ordered_ids: ids, view_id: VIEW_ID });
  }

  function submitApproval(event: FormEvent) {
    event.preventDefault();
    if (!approvalMove || approvalReason.trim().length < 3) return;
    move.mutate(
      { id: approvalMove.card.id, lane: approvalMove.lane, reason: approvalReason, view_id: VIEW_ID },
      {
        onSuccess: (result) => {
          if (!result.ok) {
            setNotice(result.reason ?? "Freigabeantrag konnte nicht angelegt werden.");
            return;
          }
          setNotice(result.reason ?? "Freigabeantrag angelegt; OpenProject blieb unverändert.");
          setApprovalMove(null);
          setApprovalReason("");
          setShowApprovals(true);
        },
        onError: () => setNotice("Freigabeantrag konnte nicht angelegt werden."),
      },
    );
  }

  function submitNew(event: FormEvent) {
    event.preventDefault();
    if (!newCard.title.trim()) return;
    create.mutate(newCard, {
      onSuccess: () => {
        setNewCard({ title: "", body: "", lane: "offen", ressort: "", due_date: "" });
        setShowNew(false);
      },
    });
  }

  function saveBoardConfig() {
    saveConfig.mutate(
      { view: VIEW_ID, config: { columns: configColumns, swimlanes_enabled: swimlanes } },
      { onSuccess: () => { setShowConfig(false); setNotice("Board-Ansicht gespeichert."); } },
    );
  }

  function moveConfigColumn(index: number, delta: number) {
    const next = [...configColumns];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setConfigColumns(next);
  }

  function renderCard(card: KanbanCard, column: KanbanColumn) {
    return (
      <article
        key={card.id}
        className={`kb-card ${SOURCE_CLASS[card.source]} ${isOverdue(card) ? "kb-overdue" : ""}`}
        draggable
        onDragStart={(event) => { setDragged(card); event.dataTransfer.effectAllowed = "move"; }}
        onDragEnd={() => { setDragged(null); setOverLane(null); }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => dropBefore(event, card, column)}
      >
        <div className="kb-card-top">
          <span className={`kb-chip ${SOURCE_CLASS[card.source]}`}>{SOURCE_LABEL[card.source]}</span>
          {card.readonly && <span className="kb-ro" title="Quellsystem bleibt unverändert">RO</span>}
          {card.programm && <span className="kb-tag">Prog. {card.programm}</span>}
          {card.priority && <span className={`kb-tag kb-prio-${card.priority}`}>{card.priority}</span>}
        </div>
        <div className="kb-card-title">{card.title}</div>
        {card.body && <div className="kb-card-body">{card.body}</div>}
        <div className="kb-card-meta">
          {card.ressort && <span>{card.ressort}</span>}
          {card.tp && <span>· {card.tp}</span>}
          {card.status_raw && <span className="kb-status">{card.status_raw}</span>}
          {card.due_date && <span className={isOverdue(card) ? "kb-due-over" : "kb-due"}>fällig {card.due_date}</span>}
        </div>
        {card.intervention && <Link to={card.intervention.route} className="kb-intv">{card.intervention.name}</Link>}
        <CardLinks links={card.links} />
        {card.source === "eigen" && (
          <div className="kb-card-actions">
            <button className="kb-del" title="Karte löschen" onClick={() => {
              if (confirm("Diese eigene Karte löschen?")) del.mutate(card.id);
            }}>Löschen</button>
          </div>
        )}
      </article>
    );
  }

  function renderColumn(column: KanbanColumn, cards: KanbanCard[], keyPrefix: string) {
    const wipExceeded = Boolean(column.wip_limit && cards.length > column.wip_limit);
    return (
      <div
        key={`${keyPrefix}:${column.key}`}
        className={`kb-col ${overLane === `${keyPrefix}:${column.key}` ? "kb-col-over" : ""}`}
        onDragOver={(event) => { event.preventDefault(); setOverLane(`${keyPrefix}:${column.key}`); }}
        onDragLeave={() => setOverLane((value) => value === `${keyPrefix}:${column.key}` ? null : value)}
        onDrop={() => dropIntoLane(column.key)}
      >
        <div className="kb-col-head">
          <strong>{column.label}</strong>
          <span className={`kb-count ${wipExceeded ? "kb-count-wip" : ""}`}>{cards.length}</span>
        </div>
        {wipExceeded && <div className="kb-wip">WIP-Limit {column.wip_limit} überschritten</div>}
        <div className="kb-col-body">
          {cards.map((card) => renderCard(card, column))}
          {cards.length === 0 && <div className="kb-col-empty">—</div>}
        </div>
      </div>
    );
  }

  return (
    <section className="kb-page">
      <header className="kb-head">
        <div>
          <h1>Kanban-Board</h1>
          <p className="kb-sub">Arbeitspakete, Prozess-Runs, Entscheidungen, nächste Schritte und persönliche Karten.</p>
        </div>
        <div className="kb-head-actions">
          <button className="btn" onClick={() => setShowNew((value) => !value)}>{showNew ? "Abbrechen" : "+ Neue Karte"}</button>
          <button className="btn ghost" onClick={() => setShowApprovals((value) => !value)}>
            Freigaben{(approvals.data?.stats.approvals_open ?? 0) > 0 ? ` (${approvals.data!.stats.approvals_open})` : ""}
          </button>
          <button className="btn ghost" onClick={() => setShowConfig((value) => !value)}>Einstellungen</button>
          <button className="btn ghost kb-icon-btn" onClick={() => board.refetch()} title="Aktualisieren">↻</button>
        </div>
      </header>

      {showNew && (
        <form className="kb-newcard" onSubmit={submitNew}>
          <input className="kb-in" placeholder="Titel der Karte *" value={newCard.title}
            onChange={(event) => setNewCard({ ...newCard, title: event.target.value })} autoFocus />
          <input className="kb-in" placeholder="Beschreibung" value={newCard.body}
            onChange={(event) => setNewCard({ ...newCard, body: event.target.value })} />
          <input className="kb-in" placeholder="Ressort/TP" value={newCard.ressort}
            onChange={(event) => setNewCard({ ...newCard, ressort: event.target.value })} />
          <select className="kb-in" value={newCard.lane} onChange={(event) => setNewCard({ ...newCard, lane: event.target.value })}>
            {(data?.lanes ?? []).filter((lane) => lane.enabled !== false).map((lane) => <option key={lane.key} value={lane.key}>{lane.label}</option>)}
          </select>
          <input className="kb-in" type="date" value={newCard.due_date} title="Fällig am"
            onChange={(event) => setNewCard({ ...newCard, due_date: event.target.value })} />
          <button className="btn" type="submit" disabled={create.isPending || !newCard.title.trim()}>Anlegen</button>
        </form>
      )}

      {showConfig && (
        <section className="kb-config" aria-label="Board-Einstellungen">
          <div className="kb-config-head">
            <strong>Board-Ansicht</strong>
            <div className="kb-segments" role="group" aria-label="Darstellung">
              <button className={!swimlanes ? "active" : ""} onClick={() => setSwimlanes(false)}>Board</button>
              <button className={swimlanes ? "active" : ""} onClick={() => setSwimlanes(true)}>Ressorts</button>
            </div>
          </div>
          <div className="kb-config-columns">
            {configColumns.map((column, index) => (
              <div className="kb-config-row" key={column.key}>
                <label className="kb-check"><input type="checkbox" checked={column.enabled !== false}
                  onChange={(event) => setConfigColumns(configColumns.map((item, itemIndex) => itemIndex === index ? { ...item, enabled: event.target.checked } : item))} /> sichtbar</label>
                <input className="kb-in" value={column.label}
                  onChange={(event) => setConfigColumns(configColumns.map((item, itemIndex) => itemIndex === index ? { ...item, label: event.target.value } : item))} />
                <label className="kb-wip-input">WIP <input className="kb-in" type="number" min="0" max="999" value={column.wip_limit ?? 0}
                  onChange={(event) => setConfigColumns(configColumns.map((item, itemIndex) => itemIndex === index ? { ...item, wip_limit: Number(event.target.value) } : item))} /></label>
                <button className="btn ghost kb-icon-btn" title="Nach links" onClick={() => moveConfigColumn(index, -1)} disabled={index === 0}>←</button>
                <button className="btn ghost kb-icon-btn" title="Nach rechts" onClick={() => moveConfigColumn(index, 1)} disabled={index === configColumns.length - 1}>→</button>
              </div>
            ))}
          </div>
          <div className="kb-config-actions">
            <button className="btn" onClick={saveBoardConfig} disabled={saveConfig.isPending}>Speichern</button>
            <button className="btn ghost" onClick={() => setShowConfig(false)}>Schließen</button>
          </div>
        </section>
      )}

      {showApprovals && (
        <section className="kb-approvals" aria-label="OpenProject-Freigaben">
          <div className="kb-approvals-head">
            <div><strong>OpenProject-Freigaben</strong><span>OpenProject bleibt unverändert</span></div>
            <button className="btn ghost kb-icon-btn" onClick={() => approvals.refetch()} title="Aktualisieren">↻</button>
          </div>
          {approvals.data?.items.length === 0 && <div className="kb-col-empty">Keine Freigabeanträge.</div>}
          <div className="kb-approval-list">
            {(approvals.data?.items ?? []).map((approval) => (
              <article className="kb-approval-row" key={approval.id}>
                <div className="kb-approval-main">
                  <strong>{approval.title || `Arbeitspaket #${approval.work_package_id}`}</strong>
                  <span>{approval.from_status || approval.from_lane} → {approval.to_status}</span>
                  <p>{approval.reason}</p>
                </div>
                <div className="kb-approval-side">
                  <span className={`kb-approval-status kb-approval-${approval.status}`}>{approvalStatus(approval.status)}</span>
                  {approval.status === "offen" && role === "berater" && (
                    <div className="kb-approval-actions">
                      <button className="btn small" disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: approval.id, action: "approve" })}>Freigeben</button>
                      <button className="btn ghost small" disabled={decide.isPending} onClick={() => {
                        const note = prompt("Begründung für die Ablehnung:") ?? "";
                        if (note.trim()) decide.mutate({ id: approval.id, action: "reject", note });
                      }}>Ablehnen</button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <div className="kb-filters">
        <input className="kb-in" placeholder="Suche…" value={filters.q}
          onChange={(event) => setFilters({ ...filters, q: event.target.value })} />
        <select className="kb-in" value={filters.ressort} onChange={(event) => setFilters({ ...filters, ressort: event.target.value })}>
          <option value="">Alle Ressorts/TP</option>
          {(available?.ressorts ?? []).map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
        <select className="kb-in" value={filters.programm} onChange={(event) => setFilters({ ...filters, programm: event.target.value })}>
          <option value="">Alle Programme</option>
          {(available?.programme ?? []).map((value) => <option key={value} value={value}>Programm {value}</option>)}
        </select>
        <select className="kb-in" value={filters.type} onChange={(event) => setFilters({ ...filters, type: event.target.value })}>
          <option value="">Alle Typen</option>
          {(available?.types ?? []).map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
        <label className="kb-check"><input type="checkbox" checked={filters.include_next !== false}
          onChange={(event) => setFilters({ ...filters, include_next: event.target.checked })} /> Nächste Schritte</label>
      </div>

      {legend.length > 0 && (
        <div className="kb-legend">
          {legend.map((item) => <span key={item.key} className={`kb-chip ${SOURCE_CLASS[item.key]}`}>{item.label} · {item.count}</span>)}
          {data && <span className="kb-legend-total">gesamt {data.total} Karten{data.hidden_count ? ` · ${data.hidden_count} in ausgeblendeten Spalten` : ""}</span>}
        </div>
      )}

      {notice && <div className="kb-notice" role="status">{notice}<button className="kb-notice-x" onClick={() => setNotice("")}>×</button></div>}
      {board.isLoading && <div className="kb-empty">Board wird geladen…</div>}
      {board.isError && <div className="kb-empty">Board konnte nicht geladen werden.</div>}
      {data?.degraded && !data.wp_available && <div className="kb-notice">Arbeitspakete sind derzeit nicht lesbar; die übrigen Quellen bleiben verfügbar.</div>}

      {data && !swimlanes && (
        <div className="kb-board">{data.columns.map((column) => renderColumn(column, column.cards, "flat"))}</div>
      )}
      {data && swimlanes && (
        <div className="kb-swimlanes">
          {data.swimlanes.map((swimlane) => (
            <section className="kb-swimlane" key={swimlane.key}>
              <header><strong>{swimlane.label}</strong><span>{swimlane.count}</span></header>
              <div className="kb-board">
                {data.columns.map((column) => renderColumn(
                  column,
                  column.cards.filter((card) => (card.ressort || "").trim() === (swimlane.key === "ohne_ressort" ? "" : swimlane.label)),
                  swimlane.key,
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {approvalMove && (
        <div className="kb-modal-backdrop" role="presentation">
          <form className="kb-modal" onSubmit={submitApproval} role="dialog" aria-modal="true" aria-labelledby="kb-approval-title">
            <h2 id="kb-approval-title">Statuswechsel beantragen</h2>
            <p><strong>{approvalMove.card.title}</strong></p>
            <p>{approvalMove.card.status_raw || approvalMove.card.lane} → {data?.lanes.find((lane) => lane.key === approvalMove.lane)?.label || approvalMove.lane}</p>
            <label className="kb-modal-field"><span>Begründung *</span>
              <textarea className="kb-in" rows={4} autoFocus value={approvalReason}
                onChange={(event) => setApprovalReason(event.target.value)} /></label>
            <div className="kb-config-actions">
              <button className="btn" type="submit" disabled={approvalReason.trim().length < 3 || move.isPending}>Antrag anlegen</button>
              <button className="btn ghost" type="button" onClick={() => setApprovalMove(null)}>Abbrechen</button>
            </div>
          </form>
        </div>
      )}

      <footer className="kb-foot">Quelle: OpenProject (read-only), Prozess-Runs, Entscheidungs-Log, proaktive KI und persönliche Karten.</footer>
    </section>
  );
}
